import React, { useEffect, useState } from "react";
import { useCart } from "../CartContext/CartContext";
import { useNavigate, Link } from "react-router-dom";
import { Box, Container, Typography, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { FaArrowLeft } from "react-icons/fa";
import axios from "axios";
import getImageUrl from "../../utils/getImageUrl";

const CartPage = () => {
  const { cartItems, removeFromCart, clearCart, updateCartItemQuantity } = useCart();
  const navigate = useNavigate();

  const [totalPrice, setTotalPrice] = useState(0);
  const [stockData, setStockData] = useState({});
  const [stockWarnings, setStockWarnings] = useState({});

  useEffect(() => {
    const fetchStock = async () => {
      const updatedStock = {};
      const warnings = {};

      for (const item of cartItems) {
        try {
          const response = await axios.get(`/api/products/${item.productId}/stock`, {
            params: { sizeId: item.sizeId },
          });

          const key = `${item.productId}_${item.sizeId}`;
          const stock = response.data.stock || 0;
          updatedStock[key] = stock;

          if (stock === 0 && item.quantity !== 0) {
            updateCartItemQuantity(item.productId, item.sizeId, 0);
            warnings[key] = "Out of stock";
          } else if (item.quantity > stock) {
            updateCartItemQuantity(item.productId, item.sizeId, stock);
            warnings[key] = `Stock adjusted to ${stock}`;
          } else {
            warnings[key] = "";
          }

        } catch (err) {
          console.error("Error fetching stock:", err);
        }
      }

      setStockData(updatedStock);
      setStockWarnings(warnings);
    };

    fetchStock();
  }, [cartItems]);

  useEffect(() => {
    const total = cartItems.reduce((sum, item) => {
      const price = Number(item.price) || 0;
      const quantity = Number(item.quantity) || 1;
      return sum + price * quantity;
    }, 0);
    setTotalPrice(total);
  }, [cartItems]);

  const isLoggedIn = localStorage.getItem("token");

  if (cartItems.length === 0) {
    return (
      <>
        <div className="breadcrumb-wrap bg-f br-2">
          <div className="container">
            <div className="breadcrumb-title"  style={{marginBottom:'-90px'}}>
              <h2>Shopping Cart</h2>
              <ul className="breadcrumb-menu list-style">
                <li><Link to="/">Home</Link></li>
                <li>Shopping Cart</li>
              </ul>
            </div>
          </div>
        </div>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4, mt: 2 }}>
            <h6 className="fw-normal mb-0">
              <Link to="/products"><FaArrowLeft /> Back to list</Link>
            </h6>
          </Box>
          <Typography>Your cart is empty.</Typography>
        </Container>
      </>
    );
  }

  return (
    <>
      <div className="breadcrumb-wrap bg-f br-2">
        <div className="container">
          <div className="breadcrumb-title"  style={{marginBottom:'-90px'}}>
            <h2>Shopping Cart</h2>
            <ul className="breadcrumb-menu list-style">
              <li><Link to="/">Home</Link></li>
              <li>Shopping Cart</li>
            </ul>
          </div>
        </div>
      </div>

      <section className="h-100">
        <div className="container h-100 py-5">
          <div className="row d-flex justify-content-center align-items-center h-100">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h6 className="fw-normal mb-0">
                  <Link to="/products"><FaArrowLeft /> Back to list</Link>
                </h6>
              </div>

              {cartItems.map((item, index) => {
                const key = `${item.productId}_${item.sizeId}`;
                const maxQuantity = stockData[key] ?? Infinity;
                const isOutOfStock = maxQuantity === 0;
                const warning = stockWarnings[key] || "";

                return (
                  <div className="card rounded-3 mb-4 cartcontiner" key={index}>
                    <div className="card-body p-4">
                      <div className="row d-flex justify-content-between align-items-center">
                        <div className="col-md-2 col-lg-2 col-xl-2">
                          <img
                            src={getImageUrl(item.image)}
                            alt={item.productName}
                            className="img-fluid rounded-3"
                            style={{ height: "200px" }}
                          />
                        </div>
                        <div className="col-md-3 col-lg-2 col-xl-2">
                          <p style={{ fontSize: "18px", textAlign: "justify" }}>{item.productName}</p>
                        </div>
                        <div className="col-md-3 col-lg-2 col-xl-2">
                          <p style={{ fontSize: "18px" }}>Size: {item.size}</p>
                          <div>
                            <label style={{ fontWeight: "bold", marginBottom: "4px" }}>Quantity:</label>
                           <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginTop: "5px" }}>
  <button
    type="button"
    style={buttonStyle}
    onClick={() => {
      if (!isOutOfStock && item.quantity > 1) {
        updateCartItemQuantity(item.productId, item.sizeId, item.quantity - 1);
      }
    }}
  >-</button>

  <span style={quantityStyle}>
    {isOutOfStock ? 0 : item.quantity}
  </span>

  <button
    type="button"
    style={buttonStyle}
    onClick={() => {
      if (!isOutOfStock && item.quantity < maxQuantity) {
        updateCartItemQuantity(item.productId, item.sizeId, item.quantity + 1);
      } else if (item.quantity >= maxQuantity) {
        setStockWarnings(prev => ({
          ...prev,
          [key]: `You have reached the max available stock: ${maxQuantity}`
        }));
      }
    }}
  >+</button>
</div>

                            {warning && (
                              <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>{warning}</p>
                            )}
                          </div>
                        </div>

                        <div className="col-md-3 col-lg-2 col-xl-2">
                          <h5 className="mb-0">₹{(parseFloat(item.price) * item.quantity).toFixed(2)}</h5>
                        </div>

                        <div className="col-md-3 col-lg-2 col-xl-2">
                          <IconButton color="error" onClick={() => removeFromCart(item.productId, item.sizeId)}>
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="card">
                <div className="card-body">
                  <div className="row d-flex justify-content-between align-items-center cart-total-button-wrap">
                    <div className="col-md-4 col-12 mb-2" >
                      <h6  style={{fontSize:'20px'}}>Total: ₹{totalPrice.toFixed(2)}</h6>
                    </div>
                    <div className="col-md-4 col-12 mb-2">
                      <button className="btn btn-primary btn-sm w-100" onClick={clearCart}>
                        CLEAR CART
                      </button>
                    </div>
                    <div className="col-md-4 col-12 mb-2">
                      <button
                        className="btn btn-primary btn-sm w-100"
                        onClick={() => {
                          if (isLoggedIn) {
                            navigate("/checkout");
                          } else {
                            navigate("/login", { state: { from: "/checkout" } });
                          }
                        }}
                      >
                        {isLoggedIn ? "CHECKOUT" : "LOGIN TO CHECKOUT"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const buttonStyle = {
  width: "32px",
  height: "32px",
  fontSize: "18px",
  fontWeight: "bold",
  border: "1px solid #ccc",
  backgroundColor: "#f0f0f0",
  color: "#000",
  borderRadius: "4px",
  cursor: "pointer",
};

const quantityStyle = {
  minWidth: "24px",
  textAlign: "center",
  fontSize: "16px",
};

export default CartPage;
